// Single source of truth for copy used across the marketing site.
// Edit here to update services, projects, partner brands, and shared contact info.
// Image references are SLUGS — see /public/images/optimized/<slug>/ and
// /src/data/imageManifest.json (generated by `yarn images`).

export const BRAND = {
    name: "Advanced Connected Technologies",
    shortName: "ACT",
    tagline: "Luxury Smart Home Integration",
    phone: "803.728.9097",
    phoneHref: "tel:+18037289097",
    phoneSchema: "+1-803-728-9097",
    email: "concierge@actluxe.net",
    emailHref: "mailto:concierge@actluxe.net",
    region: "Private Residences Worldwide · Based in the United States",
};

export const NAV_LINKS = [
    { label: "Services", to: "/services" },
    { label: "Brands", to: "/brands" },
    { label: "Process", to: "/process" },
    { label: "About", to: "/about" },
    { label: "Insights", to: "/insights" },
    { label: "Contact", to: "/contact" },
];

// Hero image slug (above-the-fold LCP on /)
export const HERO_SLUG = "hero_luxury_smart_home";

export const SERVICES = [
    {
        slug: "home-automation",
        number: "01",
        name: "Home Automation & Control",
        icon: "CircuitBoard",
        short:
            "A single, intuitive interface composing every system within the residence.",
        tagline: "One considered gesture. The entire home responds.",
        hero: "control_panel",
        intro:
            "The most refined residences disappear into themselves. Our automation practice brings climate, lighting, audio, security, and shading into one quiet, tactile language—authored around the cadence of your day and expressed through a single, beautifully restrained interface.",
        included: [
            "Certified Control4 and Savant programming tailored to the architecture",
            "Scene design calibrated to the choreography of morning, evening, entertaining, and rest",
            "Keypad and touchpanel layouts designed in collaboration with your interior team",
            "Voice, tablet, and remote access with white-glove onboarding for household staff",
            "Integration with HVAC, audio, video, shading, and access control as a unified system",
            "Concierge-tier lifetime support and proactive software stewardship",
        ],
        approach: [
            {
                step: "Discovery",
                copy:
                    "An on-site conversation with principals, designers, and architects to understand how the home is truly used.",
            },
            {
                step: "Design",
                copy:
                    "A written control narrative and room-by-room specification, reviewed alongside your design team before a cable is pulled.",
            },
            {
                step: "Delivery",
                copy:
                    "Discreet, phased installation followed by quiet commissioning, principal walkthroughs, and a lifetime of stewardship.",
            },
        ],
        imagery: ["control_panel", "living_room"],
    },
    {
        slug: "lighting-shades",
        number: "02",
        name: "Lighting & Motorized Shades",
        icon: "SunMedium",
        short:
            "Architectural light and silent shading that follow the sun and the evening.",
        tagline: "Architecture, revealed by the considered hand of light.",
        hero: "moody_lit",
        intro:
            "Light is the most underestimated material in a residence. We collaborate with architects and lighting designers to deliver tunable, circadian environments and near-silent motorized shading that respond to hour, mood, and occasion—invisible systems in service of the room.",
        included: [
            "Lutron HomeWorks and Ketra tunable-white design and commissioning",
            "Hardwired, low-voltage, and architectural fixture integration",
            "Scene libraries for morning, dining, cinema, entertaining, and late-evening retreat",
            "Silent motorized drapery, roller shades, and skylight treatments",
            "Solar-tracking and astronomical scheduling across every façade",
            "Dedicated keypads engraved and finished to match the interior program",
        ],
        approach: [
            {
                step: "Survey",
                copy:
                    "A lighting walk with the interior and architectural team to understand intent, fixture schedule, and daylight behavior.",
            },
            {
                step: "Composition",
                copy:
                    "Scenes and timeclocks are drafted on paper, then rendered against your drawings before any programming begins.",
            },
            {
                step: "Calibration",
                copy:
                    "Every keypad, fixture, and shade is tuned on-site after occupancy, then refined over the seasons as the home settles.",
            },
        ],
        imagery: ["moody_lit", "corridor"],
    },
    {
        slug: "home-theater-audio",
        number: "03",
        name: "Home Theater & Distributed Audio",
        icon: "Music2",
        short:
            "Reference-grade cinema and near-invisible audio throughout the home.",
        tagline: "Sound as architecture. Cinema as occasion.",
        hero: "home_theater",
        intro:
            "A properly authored room is a private occasion. We design reference-grade theaters acoustically treated from the walls inward, alongside distributed audio programs that are felt more than seen—Bang & Olufsen, K-Array, and Sonos, installed with a restraint that honors the interior design.",
        included: [
            "Dolby Atmos theater design with acoustic modeling and seating ergonomics",
            "Kaleidescape movie libraries and 4K / 8K projection commissioning",
            "Multi-room, multi-source distributed audio with imperceptible in-ceiling loudspeakers",
            "Collaboration with acoustic, millwork, and interior teams on finish and fabric",
            "Architectural integration of subwoofers, amplifier closets, and equipment racks",
            "Dedicated calibration sessions with measurement microphones and principal review",
        ],
        approach: [
            {
                step: "Program",
                copy:
                    "We determine how the room will be used—film, music, sport—and draft seating, sightlines, and acoustic treatment accordingly.",
            },
            {
                step: "Construction",
                copy:
                    "Close coordination with the builder: risers, isolation, wiring, and HVAC silencing are engineered before finishes arrive.",
            },
            {
                step: "Calibration",
                copy:
                    "Industry-standard measurement and listening sessions until the room sounds exactly the way the room should.",
            },
        ],
        imagery: ["home_theater", "living_room"],
    },
    {
        slug: "security-surveillance",
        number: "04",
        name: "Security & Surveillance",
        icon: "ShieldCheck",
        short:
            "Discreet, enterprise-grade protection for the residence and estate.",
        tagline: "Quiet vigilance. Absolute discretion.",
        hero: "entry_gate",
        intro:
            "Security at this level is defined by what is never seen. We design layered, enterprise-grade protection—access control, intrusion, and cinematic surveillance—engineered to protect principals and family while preserving the private, unforced atmosphere of the home.",
        included: [
            "Monitored intrusion systems with cellular, IP, and radio redundancies",
            "Access control for gates, service entries, staff quarters, and private wings",
            "4K IP surveillance with license-plate, facial, and perimeter analytics",
            "Intercom and visitor management integrated with concierge and estate staff",
            "Secure off-site video retention with retention policies appropriate to the household",
            "Coordination with private security details and executive-protection providers",
        ],
        approach: [
            {
                step: "Assessment",
                copy:
                    "A discreet site survey—often under NDA—to understand risk posture, household routine, and property envelope.",
            },
            {
                step: "Design",
                copy:
                    "A layered security architecture is drawn up, reviewed with the principal's security advisor, and refined before any trade work.",
            },
            {
                step: "Stewardship",
                copy:
                    "Ongoing monitoring, credential management, and quiet annual reviews to keep the system aligned with how the family lives.",
            },
        ],
        imagery: ["entry_gate", "architectural"],
    },
    {
        slug: "networking-wifi",
        number: "05",
        name: "Networking & Enterprise-Grade Wi-Fi",
        icon: "Network",
        short:
            "Fiber backbones and seamless wireless throughout every wing of the estate.",
        tagline: "The quiet current beneath every other system.",
        hero: "network_rack",
        intro:
            "Every system in the modern residence is downstream of the network. We build enterprise-grade, fiber-backbone infrastructure with Ubiquiti equipment—engineered for absolute reliability, continuous wireless coverage, and a household that never notices the seam.",
        included: [
            "Fiber and Cat6A structured cabling designed with the electrical engineer",
            "Ubiquiti Wi-Fi 6E / 7 deployment with seamless roaming",
            "Segmented VLANs for family, staff, guest, IoT, and estate operations",
            "Enterprise firewall, content filtering, and VPN for principals' travel",
            "Carrier-diverse internet with automatic failover and 4G/5G redundancy",
            "Proactive monitoring with silent remediation before issues reach the household",
        ],
        approach: [
            {
                step: "Survey",
                copy:
                    "Predictive coverage modeling and site RF analysis inform every access-point location before construction.",
            },
            {
                step: "Backbone",
                copy:
                    "We specify the pathway, racking, and thermal envelope of the head-end room alongside the GC and electrical engineer.",
            },
            {
                step: "Assurance",
                copy:
                    "Continuous monitoring and seasonal tuning keep performance invisible and uninterrupted.",
            },
        ],
        imagery: ["network_rack", "architectural"],
    },
    {
        slug: "commercial",
        number: "06",
        name: "Commercial & Estate Integration",
        icon: "Building2",
        short:
            "Tailored integration for offices, hospitality, yachts, and multi-residence portfolios.",
        tagline: "A single standard across every property you own.",
        hero: "yacht",
        intro:
            "For principals who entrust us beyond a single residence, we extend the same standard to private offices, family foundations, superyachts, and multi-residence portfolios—so that every environment feels, operates, and is stewarded to an identical, uncompromising specification.",
        included: [
            "Unified control and network standards across residences, offices, and yachts",
            "Boardroom, video-conferencing, and executive-suite AV integration",
            "Concierge hospitality experiences for private clubs and guest residences",
            "Marine integration in partnership with yard and refit teams",
            "Estate-wide monitoring, staff training, and 24/7 concierge support",
            "Confidential project delivery under NDA and executive-protection protocols",
        ],
        approach: [
            {
                step: "Portfolio Review",
                copy:
                    "A confidential audit of every property's current systems, staff workflows, and continuity gaps.",
            },
            {
                step: "Standardization",
                copy:
                    "A single technical specification authored across the portfolio so staff and principals encounter one consistent experience.",
            },
            {
                step: "Concierge",
                copy:
                    "A dedicated account lead, quarterly stewardship reviews, and a direct line to engineering without a ticket between.",
            },
        ],
        imagery: ["yacht", "architectural"],
    },
];

export const FEATURED_PROJECTS = [
    {
        slug: "hillside-residence",
        name: "The Hillside Residence",
        location: "Beverly Hills, California",
        image: "hillside_residence",
        scope: ["Automation", "Lighting", "Security"],
        size: "large",
    },
    {
        slug: "alpine-chalet",
        name: "Alpine Chalet",
        location: "Aspen, Colorado",
        image: "home_theater",
        scope: ["Home Theater", "Distributed Audio", "Networking"],
        size: "small",
    },
    {
        slug: "oceanfront-estate",
        name: "Oceanfront Estate",
        location: "Palm Beach, Florida",
        image: "control_panel",
        scope: ["Control Room", "Automation", "Surveillance"],
        size: "small",
    },
];

export const PARTNER_BRANDS = [
    "Control4",
    "Savant",
    "Josh.ai",
    "Lutron",
    "CoeLux",
    "Sonos",
    "Bang & Olufsen",
    "K-Array",
    "Sony",
    "Samsung",
    "Kaleidescape",
    "Ubiquiti",
];

export const PHILOSOPHY_POINTS = [
    {
        title: "Certified Technicians",
        copy:
            "Every installer holds current manufacturer certifications across Control4, Savant, and Lutron.",
    },
    {
        title: "Concierge Service",
        copy:
            "A dedicated project lead, known to the household, present from first conversation through the years that follow.",
    },
    {
        title: "Lifetime Support",
        copy:
            "Proactive software stewardship, seasonal reviews, and a direct line to engineering for the life of the residence.",
    },
    {
        title: "Unmatched Discretion",
        copy:
            "NDA-first engagements, coordinated with principals' private security and household staff.",
    },
];

// Editorial image used by the Home page Philosophy split.
export const PHILOSOPHY_IMAGE = "living_room";

// Stub page copy (Phase 1 — only /contact has been replaced by a real page).
export const STUB_COPY = {
    process: {
        eyebrow: "Process",
        headline: "A deliberate, documented path from first call to final calibration.",
        body:
            "Discovery, design, delivery, and lifetime stewardship—the full process will be published here in the coming weeks.",
    },
    about: {
        eyebrow: "About",
        headline: "Craft, certification, and an uncommon respect for privacy.",
        body:
            "The full ACT story, leadership, and studio philosophy will arrive here in our next release.",
    },
    insights: {
        eyebrow: "Insights",
        headline: "Field notes from the residences we keep.",
        body:
            "Essays, technical briefs, and partner interviews on the quiet practice of luxury integration—forthcoming.",
    },
    contact: {
        eyebrow: "Contact",
        headline: "Private consultations, by appointment.",
        body:
            "The full concierge contact experience is arriving shortly. In the meantime, we welcome your call or email.",
    },
};
